import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { IDynamicDialogConfig } from '@app/shared/modules/dynamic-dialog/model';
import { SubscriberComponent } from '@app/shared/modules/subscriber/base/subscriber.component.base';
import { AmplitudeService } from '@app/shared/services/amplitude.service';
import { CollectionService } from '@app/shared/services/collection/collection.service';
import { ContractContext } from '@app/shared/services/context/contract.context.service';
import { ModalService } from '@app/shared/services/modal.service';
import { ACTION_EVENT_PROPERTY, ACTION_NAME, AMPLITUDE_EVENT } from '@app/shared/types/enums/amplitude';
import { AuthService } from '@auth0/auth0-angular';
import moment from 'moment';
import { catchError, EMPTY, map, Observable } from 'rxjs';
import { TAB_PAYMENTS_COMPONENT } from './billing-periods-modal.enum';
import { IBillingPeriods } from '../../../../../models/colletion.model';
import { AppContext } from '@app/shared/services/context/app.context.service';

@Component({
  selector: 'app-billing-periods-modal',
  templateUrl: './billing-periods-modal.component.html',
  styleUrls: ['./billing-periods-modal.component.sass'],
})
export class BillingPeriodsModalComponent extends SubscriberComponent implements OnInit, OnDestroy {
  billPeriodsData$: Observable<IBillingPeriods[] | undefined> = EMPTY;
  initialState!: IDynamicDialogConfig;
  filterDateForm!: FormGroup;
  minDate = moment('1800-01-01').toDate();
  maxDate = moment().endOf('year').toDate();
  contractCode = '';
  textDateHeader = '';
  language: string;

  constructor(
    private readonly _appCtxSvc: AppContext,
    private readonly _modalSvc: ModalService,
    private readonly _contractContext: ContractContext,
    private readonly _amplitudeSvc: AmplitudeService,
    private readonly _authSvc: AuthService,
    private readonly _collectionSvc: CollectionService
  ) {
    super();
    this.language = this._appCtxSvc.getLanguage();
  }

  ngOnInit(): void {
    this.logInitEvent();
    this._subscriptions.sink = this._contractContext.selectedContract$.subscribe(contract => {
      if (contract) {
        this.contractCode = contract?.contract_code;
      }
    });
    this.createForm();
    this.getModalData();
  }

  private logInitEvent(): void {
    this._amplitudeSvc.logEvent<ACTION_EVENT_PROPERTY>(AMPLITUDE_EVENT.ACTION_INIT, {
      actionName: ACTION_NAME.BILLING_PERIODS,
    });
  }

  reload() {
    this.setupSubscriptions();
  }

  setNewDate(event: string) {
    if (event) {
      this.setupSubscriptions();
    }
  }

  createForm() {
    const currentMonth = moment().endOf('month').toDate();
    this.filterDateForm = new FormGroup({
      date: new FormControl(currentMonth, [Validators.required]),
    });
  }

  getModalData() {
    this._subscriptions.sink = this._modalSvc.getModalData().subscribe({
      next: (data: IDynamicDialogConfig) => {
        if (data.componentName === TAB_PAYMENTS_COMPONENT.BILLING_PERIODS) {
          this.setupSubscriptions();
        }
      },
    });
  }

  private setupSubscriptions() {
    this._subscriptions.sink = this._authSvc.user$.subscribe({
      next: agentData => {
        if (agentData) {
          let dateForm = this.filterDateForm.get('date')?.value;
          let year = moment(dateForm).format('YYYY');
          let month = moment(dateForm).format('MM');
          this.textDateHeader = moment(dateForm).locale(this.language.toLocaleLowerCase()).format('MMMM YYYY');
          this.billPeriodsData$ = this._collectionSvc.getBillingPeriods(this.contractCode, year, month).pipe(
            map(response => response),
            catchError(err => {
              this.setError(false);
              throw err;
            })
          );
        }
      },
    });
  }

  ngOnDestroy() {
    this.unsubscribe();
  }
}
